import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Clock, FileText, XCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function NonEnrolledConfirmation() {
  const { registrationId } = useParams();
  const [reg, setReg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/non-enrolled/registration/${registrationId}`).then(r => setReg(r.data)).catch(() => {});
  }, [registrationId]);

  if (!reg) return <div className="min-h-screen flex items-center justify-center"><p>Loading...</p></div>;

  const status = reg.status;
  const isPaid = status === "confirmed";
  const isSigned = status === "signed";
  const isExpired = status === "expired" || status === "cancelled";

  let title = "Registration Received";
  let chipText = "TENTATIVE — Sign Contract";
  let chipColor = "bg-yellow-50 border-yellow-300 text-[#92400E]";
  let Icon = Clock;

  if (isPaid) {
    title = "Payment Confirmed!";
    chipText = "PAID — Spot Confirmed";
    chipColor = "bg-green-50 border-green-400 text-green-700";
    Icon = CheckCircle;
  } else if (isSigned) {
    title = "Contract Signed";
    chipText = "AWAITING PAYMENT (Cash at Camp Office)";
    chipColor = "bg-[#FDF6E3] border-[#D4A843] text-[#92400E]";
    Icon = FileText;
  } else if (isExpired) {
    title = "Reservation Released";
    chipText = status === "cancelled" ? "CANCELLED" : "EXPIRED — Re-register if you'd still like a spot";
    chipColor = "bg-red-50 border-red-300 text-red-700";
    Icon = XCircle;
  }

  const dayLabel = reg.confirmed_day?.charAt(0).toUpperCase() + reg.confirmed_day?.slice(1);
  const dates = reg.confirmed_dates || reg.choice1_dates || [];
  const lessonCount = reg.lesson_count ?? dates.length;
  const perLesson = reg.price_per_lesson ?? (lessonCount ? (reg.total_price / lessonCount) : 0);

  return (
    <div className="min-h-screen bg-white p-4 sm:p-8">
      <div className="max-w-lg mx-auto text-center">
        <img src="https://customer-assets.emergentagent.com/job_pool-lesson-book/artifacts/c9s2nl0s_rr%20logo.png" alt="RRDC" className="w-16 h-16 mx-auto mb-4" />
        <h1 className="font-heading text-2xl font-bold text-[#2B4C8C] mb-2" data-testid="ne-confirmation-title">{title}</h1>

        <div className={`rounded-2xl p-6 mt-6 border-2 ${chipColor}`}>
          <div className="flex items-center justify-center gap-2 mb-3">
            <Icon className="w-6 h-6" />
            <p className="text-lg font-bold" data-testid="ne-status-chip">{chipText}</p>
          </div>

          <div className="mt-4 text-left space-y-2 text-sm text-slate-700 bg-white/60 rounded-lg p-3">
            <p><strong>Child:</strong> {reg.child_first_name} {reg.child_last_name}</p>
            <p><strong>Day:</strong> {dayLabel}s</p>
            <p><strong>Time:</strong> {reg.confirmed_time || reg.choice1_time}</p>
            <p><strong>Instructor:</strong> {reg.assigned_instructor || reg.choice1_lifeguard_name}</p>
            <p><strong>Lessons:</strong> {lessonCount} × ${Number(perLesson).toFixed(2)} = <strong>${Number(reg.total_price).toFixed(2)}</strong></p>
            {dates.length > 0 && (
              <div>
                <p className="font-semibold mt-2">Lesson Dates:</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {dates.map((d, i) => (
                    <span key={i} className="px-2 py-0.5 bg-[#FDF6E3] border border-[#D4A843] rounded text-xs">{d}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {!isPaid && !isExpired && reg.tentative_hold_until && (
            <p className="mt-4 font-semibold text-sm">
              Payment due by: {new Date(reg.tentative_hold_until).toLocaleDateString()}
            </p>
          )}
        </div>

        {isSigned && (
          <a
            href={`${API}/api/non-enrolled/registration/${registrationId}/contract-pdf`}
            className="inline-flex items-center gap-2 mt-4 text-[#2B4C8C] hover:underline text-sm font-semibold"
            data-testid="ne-download-contract"
          >
            <FileText className="w-4 h-4" /> Download Signed Contract (PDF)
          </a>
        )}

        {!isPaid && !isExpired && (
          <p className="mt-4 text-sm text-slate-600">
            Please bring payment (cash) to the camp office within 7 days to secure your spot.
          </p>
        )}
      </div>
    </div>
  );
}
